import React from 'react';

import { translate } from '@vestahealthcare/common/i18n';
import {
  PaginationType,
  VisitSummaryItem,
} from '@vestahealthcare/common/models';
import {
  DATE_FORMAT_SHORT,
  EMPTY,
} from '@vestahealthcare/common/utils/constants';

import { MemberInfo, Table } from 'styleguide-v2';

interface Props {
  defaultPageSize?: number;
  defaultSort?: string;
  items: VisitSummaryItem[];
  pagination?: PaginationType;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onDefaultSort: () => void;
  onSort: (field: string, direction: string) => void;
}

const TABLE_HEADERS = {
  attempts: translate('visitsSummary.table.attempts'),
  language: translate('visitsSummary.table.language'),
  lastApptDate: translate('visitsSummary.table.lastApptDate'),
  lastApptType: translate('visitsSummary.table.lastApptType'),
  member: translate('visitsSummary.table.member'),
  nextApptDate: translate('visitsSummary.table.nextApptDate'),
  nextApptProvider: translate('visitsSummary.table.nextApptProvider'),
  nextApptType: translate('visitsSummary.table.nextApptType'),
  npOwner: translate('visitsSummary.table.npOwner'),
};

const SORT_FIELDS = {
  [TABLE_HEADERS.lastApptDate]: 'lastAppointmentDate',
  [TABLE_HEADERS.lastApptType]: 'lastAppointmentType',
  [TABLE_HEADERS.nextApptDate]: 'nextAppointmentDate',
  [TABLE_HEADERS.nextApptType]: 'nextAppointmentType',
  [TABLE_HEADERS.nextApptProvider]: 'nextAppointmentProvider',
  [TABLE_HEADERS.npOwner]: 'npOwner',
};

const VISIT_SUMMARY_COLUMNS = (defaultSort?: string) => {
  const field = defaultSort?.split(' ')[0];
  const direction = defaultSort?.split(' ')[1] as 'asc' | 'desc';

  return [
    {
      headerName: TABLE_HEADERS.member,
      component: ({ member }: VisitSummaryItem) => (
        <MemberInfo link={`#/patients/${member?.id}`} member={member} />
      ),
    },
    {
      headerName: TABLE_HEADERS.language,
      component: ({ language }: VisitSummaryItem) =>
        language?.toString() ?? EMPTY,
    },
    {
      headerName: TABLE_HEADERS.npOwner,
      field: 'npOwner.fullName',
      direction:
        field === SORT_FIELDS[TABLE_HEADERS.npOwner] ? direction : undefined,
      sortable: true,
    },
    {
      headerName: TABLE_HEADERS.lastApptDate,
      component: ({ lastAppointmentDate }: VisitSummaryItem) =>
        lastAppointmentDate?.format(DATE_FORMAT_SHORT) ?? EMPTY,
      direction:
        field === SORT_FIELDS[TABLE_HEADERS.lastApptDate]
          ? direction
          : undefined,
      sortable: true,
    },
    {
      headerName: TABLE_HEADERS.lastApptType,
      field: 'lastAppointmentType',
      direction:
        field === SORT_FIELDS[TABLE_HEADERS.lastApptType]
          ? direction
          : undefined,
      sortable: true,
    },
    {
      headerName: TABLE_HEADERS.nextApptDate,
      component: ({ nextAppointmentDate }: VisitSummaryItem) =>
        nextAppointmentDate?.format(DATE_FORMAT_SHORT) ?? EMPTY,
      direction:
        field === SORT_FIELDS[TABLE_HEADERS.nextApptDate]
          ? direction
          : undefined,
      sortable: true,
    },
    {
      headerName: TABLE_HEADERS.nextApptType,
      field: 'nextAppointmentType',
      direction:
        field === SORT_FIELDS[TABLE_HEADERS.nextApptType]
          ? direction
          : undefined,
      sortable: true,
    },
    {
      headerName: TABLE_HEADERS.nextApptProvider,
      field: 'nextAppointmentProvider',
      direction:
        field === SORT_FIELDS[TABLE_HEADERS.nextApptProvider]
          ? direction
          : undefined,
      sortable: true,
    },
    {
      headerName: TABLE_HEADERS.attempts,
      field: 'attempts',
    },
  ];
};

export const VisitSummaryTable = (props: Props) => (
  <Table
    config={{
      columns: VISIT_SUMMARY_COLUMNS(props.defaultSort),
      compact: true,
      data: props.items,
      defaultPageSize: props.defaultPageSize,
      pagination: props.pagination,
      paginationOptions: [25, 50, 100],
    }}
    empty
    fixedHeader
    onChangePage={props.onChangePage}
    onChangeRowsPerPage={props.onChangePageSize}
    onDefaultSort={props.onDefaultSort}
    onSort={({ headerName, direction }) => {
      if (SORT_FIELDS[headerName as string] && direction) {
        props.onSort(SORT_FIELDS[headerName as string], direction);
      }
    }}
  />
);

export default VisitSummaryTable;
